// src/services/auth/AuthServiceImpl.ts
import { AuthService } from '@/services/abstract/AuthService';
import type {
  BeginRegistrationDTO,
  FinalizeLoginDTO,
  FinilizeVerifyDTO,
  InitLoginDTO,
  InitRegistrationDTO,
  LoginDTO,
  LoginResult,
  RecoveryVerificationDTO,
  VerificationDTO,
} from '@/types/DTOs';
import type { FetcherService } from '@/services/abstract/FetcherService';
import { authEndpoints } from '@/api/endpoints';
import { clearSessionToken } from '@/utils/sessionStorage';
import {
  createRegistrationRequest,
  performOpaqueRegistration,
} from '@/utils/opaque-registration.utils';
import { createAuthInit, performOpaqueAuth } from '@/utils/opaque-login.utils';

/**
 * Implementation of authentication service handling user login, registration,
 * and two-factor authentication flows.
 *
 * Provides complete authentication flow including:
 * - Login/logout operations
 * - Multi-step registration wizard
 * - Two-factor authentication
 *
 * @see AuthService
 */
export class AuthServiceImpl extends AuthService {
  /**
   * Creates an instance of AuthServiceImpl
   * @param fetcher - Service for making HTTP requests to the backend
   */
  constructor(private readonly fetcher: FetcherService) {
    super();
  }

  // --- Login / Logout ---

  /**
   * Authenticates a user with credentials
   * @endpoint POST /auth/login
   * @param data - Login credentials and optional 2FA information
   * @request LoginDTO - { username: string; password: string; twoFactorCode?: string }
   * @returns LoginResult containing authentication token and user information, or 2FA requirement
   * @throws AuthenticationError if credentials are invalid
   * @throws ValidationError if input data is malformed
   * @tags auth, login
   */
  async login(data: LoginDTO): Promise<LoginResult> {
    const { username, password } = data;

    // Step 1: Create OPAQUE auth initialization (KE1) and get client instance
    const { ke1, client } = await createAuthInit(password);

    // Step 2: Send KE1 to server
    const serverResponse = await this.initLogin({ username, ke1 });

    // Step 3: Process server's KE2 and generate KE3 using same client instance
    const { sessionId, ke3 } = await performOpaqueAuth(client, username, serverResponse);

    // Step 4: Finalize login and verify session key
    const { twoFactorToken } = await this.finalizeLogin({ sessionId, ke3 });

    return { twoFactorToken };
  }

  async initLogin(data: { username: string; ke1: string }): Promise<InitLoginDTO> {
    return await this.fetcher.post(authEndpoints.initLogin, data);
  }

  async finalizeLogin(data: { sessionId: string; ke3: string }): Promise<FinalizeLoginDTO> {
    return await this.fetcher.post(authEndpoints.finalizeLogin, data);
  }

  /**
   * Logs out the current user by clearing the locally stored session token.
   *
   * @returns void
   * @tags auth, logout
   */
  // eslint-disable-next-line @typescript-eslint/class-methods-use-this -- required by the abstract auth service contract
  async logout(): Promise<void> {
    clearSessionToken();
    await Promise.resolve();
  }

  // --- Registration wizard ---

  /**
   * Initiates the registration process for a new user
   * @endpoint POST /auth/begin-registration
   * @param data - Initial registration data
   * @request { username: string; otp: string }
   * @returns Object containing a registration token for subsequent steps
   * @throws ValidationError if username is already taken or OTP is invalid
   * @throws RateLimitError if too many registration attempts
   * @tags auth, registration
   */
  async beginRegistration(data: { username: string; otp: string }): Promise<BeginRegistrationDTO> {
    return await this.fetcher.post<BeginRegistrationDTO>(authEndpoints.validateOtp, data);
  }

  /**
   * Sets the password for a user during registration
   * @endpoint POST /auth/set-password
   * @param data - Registration token and desired password
   * @request { regToken: string; password: string }
   * @returns void
   * @throws ValidationError if password doesn't meet security requirements
   * @throws UnauthorizedError if registration token is invalid or expired
   * @tags auth, registration, password
   */
  async setPassword(data: { username: string; password: string }): Promise<void> {
    const { username, password } = data;
    try {
      const { sessionId, registrationRecord } = await this.prepareRegistrationRecord({
        username,
        password,
        init: async (registrationRequest) =>
          await this.initRegistration({ username, registrationRequest }),
      });

      await this.finalizeRegistration({ sessionId, registrationRecord });
    } finally {
      // SECURITY: Clear password from memory
      data.password = '';
    }
  }

  /**
   * Runs the OPAQUE registration ceremony and stops one call short of the POST.
   * @param data - Username, password, and the init round-trip to use
   * @returns The OPAQUE session id and the final registration record
   * @throws Error if any step of the ceremony fails
   * @tags auth, opaque, password
   * @see AuthService.prepareRegistrationRecord
   */
  // eslint-disable-next-line @typescript-eslint/class-methods-use-this -- ceremony is local; the one round-trip comes in via `init`
  async prepareRegistrationRecord(data: {
    username: string;
    password: string;
    init: (
      registrationRequest: string
    ) => Promise<{ sessionId: string; registrationResponse: string }>;
  }): Promise<{ sessionId: string; registrationRecord: string }> {
    const { username, password, init } = data;
    const email = username;
    try {
      // Step 1: Create OPAQUE registration request and get client instance
      const { registrationRequest, client } = await createRegistrationRequest(password);

      // Step 2: Send to server for OPRF evaluation
      const serverResponse = await init(registrationRequest);

      // Step 3: Finalize registration using the same client instance
      return await performOpaqueRegistration(client, serverResponse, email);
    } finally {
      // SECURITY: Clear password from memory
      data.password = '';
    }
  }

  /**
   * Confirms the user's email address with a verification code
   * @endpoint POST /auth/confirm-email
   * @param data - Registration token and email verification code
   * @request { regToken: string; code: string }
   * @returns void
   * @throws ValidationError if verification code is incorrect
   * @throws UnauthorizedError if registration token is invalid or expired
   * @tags auth, registration, email, verification
   */
  async confirmEmail(data: { code: string }): Promise<void> {
    await this.fetcher.post(authEndpoints.verifyEmail, data);
  }

  /**
   * Requests a new email verification code to be sent
   * @endpoint POST /auth/resend-email-code
   * @param regToken - Registration token
   * @request { regToken: string }
   * @returns void
   * @throws UnauthorizedError if registration token is invalid or expired
   * @throws RateLimitError if too many resend requests
   * @tags auth, registration, email, verification
   */
  async resendEmailCode(): Promise<VerificationDTO> {
    return await this.fetcher.post<VerificationDTO>(authEndpoints.sendEmailCode);
  }
  async sendEmailCode(): Promise<VerificationDTO> {
    return await this.fetcher.post(authEndpoints.sendEmailCode);
  }

  /**
   * Confirms the user's phone number with a verification code
   * @endpoint POST /auth/confirm-phone
   * @param data - Registration token and phone verification code
   * @request { regToken: string; code: string }
   * @returns void
   * @throws ValidationError if verification code is incorrect
   * @throws UnauthorizedError if registration token is invalid or expired
   * @tags auth, registration, phone, verification
   */
  async confirmPhone(data: { code: string }): Promise<{ message: string }> {
    return await this.fetcher.post(authEndpoints.verifyPhone, data);
  }

  /**
   * Requests a new phone verification code to be sent
   * @endpoint POST /auth/resend-phone-code
   * @param regToken - Registration token
   * @request { regToken: string }
   * @returns void
   * @throws UnauthorizedError if registration token is invalid or expired
   * @throws RateLimitError if too many resend requests
   * @tags auth, registration, phone, verification
   */
  async resendPhoneCode(): Promise<VerificationDTO> {
    return await this.fetcher.post(authEndpoints.sendPhoneCode);
  }

  async sendPhoneCode(): Promise<VerificationDTO> {
    return await this.fetcher.post(authEndpoints.sendPhoneCode);
  }

  async resendRegistrationOtp(data: { username: string }): Promise<void> {
    await this.fetcher.post(authEndpoints.resendOtp, data);
  }

  // --- Two-Factor ---

  /**
   * Selects the preferred two-factor authentication method for the session
   * @endpoint POST /private/v1/auth/2fa/choose-method
   * @param data - Session ID and selected 2FA method
   * @request { sessionToken: string; method: "sms" | "email" }
   * @returns void
   * @throws UnauthorizedError if session ID is invalid or expired
   * @throws ValidationError if selected method is not available for user
   * @tags auth, 2fa, two-factor
   */
  async selectTwoFactorMethod(data: {
    twoFactorToken: string;
    method: 'sms' | 'email';
  }): Promise<VerificationDTO> {
    return await this.fetcher.post(authEndpoints.chooseTwoFactorMethod, data);
  }

  /**
   * Verifies a two-factor authentication code and completes login
   * @endpoint POST /auth/verify-2fa
   * @param data - Session ID and 2FA verification code
   * @request { sessionToken: string; code: string }
   * @returns LoginResult containing authentication token and user information
   * @throws ValidationError if 2FA code is incorrect
   * @throws UnauthorizedError if session ID is invalid or expired
   * @tags auth, 2fa, two-factor, verification
   */
  async verifyTwoFactorCode(data: {
    code: string;
    twoFactorToken: string;
    method: 'sms' | 'email';
  }): Promise<FinilizeVerifyDTO> {
    return await this.fetcher.post(authEndpoints.verifyTwoFactor, data);
  }

  async initRegistration(data: {
    username: string;
    registrationRequest: string;
  }): Promise<InitRegistrationDTO> {
    return await this.fetcher.post(authEndpoints.initRegistration, data);
  }

  async finalizeRegistration(data: {
    sessionId: string;
    registrationRecord: string;
  }): Promise<void> {
    await this.fetcher.post(authEndpoints.finalizeRegistration, data);
  }

  /**
   * OPRF round-trip for rotating an existing OPAQUE credential.
   * @endpoint POST /private/v1/auth/credentials/rotate/init
   * @param data - The OPAQUE registration request
   * @returns The OPAQUE session id and the server's registration response
   * @tags auth, opaque, password
   * @see AuthService.rotateCredentialInit
   */
  async rotateCredentialInit(data: { registrationRequest: string }): Promise<InitRegistrationDTO> {
    return await this.fetcher.post(authEndpoints.rotateCredentialInit, data);
  }

  async forgotPassword(data: { emailAddress: string }): Promise<void> {
    await this.fetcher.post(authEndpoints.forgotPassword, data);
  }

  async verifyRecoveryOtp(data: {
    emailAddress: string;
    code: string;
  }): Promise<RecoveryVerificationDTO> {
    return await this.fetcher.post(authEndpoints.verifyRecoveryOtp, data);
  }

  async sendRecoveryTwoFactor(data: {
    recoveryToken: string;
    method: 'sms';
  }): Promise<VerificationDTO> {
    return await this.fetcher.post(authEndpoints.recoverySendTwoFactor, data);
  }

  async verifyRecoveryTwoFactor(data: {
    recoveryToken: string;
    code: string;
    method: 'sms';
  }): Promise<void> {
    await this.fetcher.post(authEndpoints.recoveryVerifyTwoFactor, data);
  }
}
